import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { naira, statusBadgeClass, statusLabel, formatDate, formatDateTime } from "../../lib/format";
import { KPI, Card, Loading } from "../../components/UI";
import { Building2, Home, DoorOpen, Wallet, AlertTriangle, TrendingUp, Users, FileSignature, Clock, BadgeCheck, Download } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { toast } from "sonner";

const COLORS = { occupied: "#0E5C3A", vacant: "#C9A227", maintenance: "#2C4E9D" };
const TYPE_COLORS = ["#0E5C3A", "#C9A227", "#2C4E9D", "#A0801C", "#5E7166"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  const exportExpiringCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/leases-expiring.csv?days=60", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leases-expiring-60d-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Expiring leases CSV downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading || !stats) return <Loading label="Loading dashboard…"/>;

  const occupancyData = [
    { name: "Occupied", value: stats.occupied, color: COLORS.occupied },
    { name: "Vacant", value: stats.vacant, color: COLORS.vacant },
    ...(stats.under_maintenance ? [{ name: "Maintenance", value: stats.under_maintenance, color: COLORS.maintenance }] : []),
  ];
  const typeData = Object.entries(stats.by_unit_type).map(([k, v]) => ({ name: k, ...v }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A227]">ADMIN • LANDLORD VIEW</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24]">Modav Homes Overview</h1>
          <p className="text-[#5E7166] mt-1 text-sm">Live data calculated from {stats.total_units} units, leases, payments and pricing.</p>
        </div>
      </div>

      {/* Anomalies banner */}
      {stats.anomalies.length > 0 && (
        <div className="card-modav p-5 border-l-4 border-[#C92A2A] bg-[#FEF0F0]/40 fade-up delay-1" data-testid="anomalies-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-[#C92A2A] flex-shrink-0 mt-0.5" size={20}/>
            <div className="flex-1">
              <div className="font-serif font-semibold text-[#C92A2A]">Data anomalies detected ({stats.anomalies.length})</div>
              <ul className="mt-2 space-y-1 text-sm text-[#1A2E24]">
                {stats.anomalies.map((a, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`anomaly-${i}`}>
                    <span className="text-[#C92A2A] mt-1">•</span>
                    <span>{a.message}</span>
                  </li>
                ))}
              </ul>
              <Link to="/units" className="text-[#0E5C3A] text-xs font-semibold mt-2 inline-flex items-center gap-1 hover:text-[#C9A227]">Review units →</Link>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Units" value={stats.total_units} icon={Home} testid="kpi-total-units"/>
        <KPI label="Occupancy Rate" value={`${stats.occupancy_rate}%`} icon={BadgeCheck} testid="kpi-occupancy-rate" hint={`${stats.occupied} occupied / ${stats.vacant} vacant`}/>
        <KPI label="Expected Rent" value={naira(stats.expected_rent)} icon={Wallet} accent testid="kpi-expected-rent"/>
        <KPI label="Collected" value={naira(stats.collected_rent)} icon={TrendingUp} testid="kpi-collected"/>
        <KPI label="Outstanding" value={naira(stats.outstanding_rent)} icon={Clock} testid="kpi-outstanding"/>
        <KPI label="Total Commission" value={naira(stats.total_commission)} icon={Wallet} accent testid="kpi-commission"/>
        <KPI label="Agreement Fees" value={naira(stats.total_agreement_fees)} icon={FileSignature} accent testid="kpi-agreement-fees"/>
        <KPI label="Open Tickets" value={stats.open_tickets} icon={Building2} testid="kpi-tickets"/>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Occupancy Breakdown" testid="chart-occupancy">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={occupancyData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={55} paddingAngle={4} stroke="none">
                {occupancyData.map((d, i) => <Cell key={i} fill={d.color}/>)}
              </Pie>
              <Tooltip/>
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle"/>
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Revenue by Unit Type" testid="chart-by-type">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={typeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE8"/>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#5E7166" }} angle={-15} textAnchor="end" height={60}/>
              <YAxis tick={{ fontSize: 11, fill: "#5E7166" }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v/1000)}k` : v}/>
              <Tooltip formatter={(v) => naira(v)}/>
              <Bar dataKey="expected_rent" fill="#0E5C3A" radius={[8,8,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Recent Payments" testid="recent-payments" action={<Link to="/payments" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">View all →</Link>}>
          {stats.recent_payments.length === 0 ? (
            <div className="text-sm text-[#5E7166] py-4">No payments recorded yet.</div>
          ) : (
            <ul className="divide-y divide-[#E5EBE8]">
              {stats.recent_payments.map(p => (
                <li key={p.id} className="py-3 flex items-center justify-between text-sm" data-testid={`recent-payment-${p.id}`}>
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="font-semibold truncate">{p.tenant_name || "Unassigned"}</div>
                    <div className="text-xs text-[#5E7166] truncate">
                      {p.unit_name && <span>{p.unit_name} • </span>}
                      {statusLabel(p.payment_type)} • {formatDateTime(p.created_at)}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="font-serif font-bold text-[#0E5C3A]">{naira(p.amount)}</div>
                    <span className={statusBadgeClass(p.payment_status)}>{statusLabel(p.payment_status)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Recent Leases" testid="recent-leases" action={<Link to="/leases" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">View all →</Link>}>
          {stats.recent_leases.length === 0 ? (
            <div className="text-sm text-[#5E7166] py-4">No leases yet. <Link className="text-[#0E5C3A] underline" to="/leases">Create one</Link>.</div>
          ) : (
            <ul className="divide-y divide-[#E5EBE8]">
              {stats.recent_leases.map(l => (
                <li key={l.id} className="py-3 flex items-center justify-between text-sm" data-testid={`recent-lease-${l.id}`}>
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="font-semibold truncate">{l.tenant_name || "Unassigned"}</div>
                    <div className="text-xs text-[#5E7166] truncate">
                      {l.unit_name || l.unit_type_name} • {formatDate(l.lease_start_date)} → {formatDate(l.lease_end_date)}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="font-serif font-bold text-[#0E5C3A]">{naira(l.total_payable)}</div>
                    <span className={statusBadgeClass(l.lease_status)}>{statusLabel(l.lease_status)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Lease expirations */}
      {stats.lease_expiring.length > 0 && (
        <Card
          title={`Leases Expiring Soon (≤ 60 days) — ${stats.lease_expiring.length}`}
          testid="lease-expiring"
          action={
            <button
              onClick={() => exportExpiringCSV()}
              className="btn-outline-emerald text-xs inline-flex items-center gap-1.5"
              data-testid="export-expiring-csv"
              title="Download CSV of all leases expiring in the next 60 days"
            >
              <Download size={14}/> Export CSV
            </button>
          }
        >
          <ul className="divide-y divide-[#E5EBE8]">
            {stats.lease_expiring.map(l => {
              const urgency = l.days_left <= 7 ? "text-[#C92A2A]" : l.days_left <= 30 ? "text-[#A0801C]" : "text-[#5E7166]";
              return (
                <li key={l.id} className="py-3 flex items-center justify-between text-sm gap-3" data-testid={`expiring-row-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate" data-testid={`expiring-tenant-${l.id}`}>
                      {l.tenant_name || "—"}
                    </div>
                    <div className="text-xs text-[#5E7166] truncate">
                      {l.unit_name || "—"} • Ends {formatDate(l.lease_end_date)}
                    </div>
                  </div>
                  <div className={`text-xs font-semibold whitespace-nowrap ${urgency}`}>
                    {l.days_left === 0 ? "Today" : `${l.days_left}d left`}
                  </div>
                  <Link to={`/leases/${l.id}`} className="btn-outline-emerald text-xs">Review</Link>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {stats.tenants_no_guarantor.length > 0 && (
        <Card title="Tenants Missing Guarantor">
          <div className="flex flex-wrap gap-2">
            {stats.tenants_no_guarantor.map(t => (
              <span key={t.id} className="badge badge-gold">{t.full_name}</span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
